// src/pages/feed/ReelsPage.tsx
//
// Best practices applied from:
//  • TikTok web   — full-viewport snap scroll, right-side actions, keyboard ↑↓ nav, progress bar
//  • Instagram    — center-column 9:16 ratio, muted-by-default, tap-to-unmute
//  • YouTube Shorts — prev/next flanking buttons, scrubber, auto-pause on out-of-view
//
import React, {
  useState, useEffect, useRef, useCallback, memo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeedStore, Post } from '@/stores/useFeedStore';
import { getImageUrl } from '@/config/media';
import { formatDistanceToNow } from 'date-fns';
import {
  Heart, MessageCircle, Share2, Volume2, VolumeX,
  Play, Pause, ChevronUp, ChevronDown, Loader2, Video,
  Send, X, Search,
} from 'lucide-react';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000   ? `${(n / 1_000).toFixed(1)}K`
  : String(n);

const CDN = 'https://media.majengo.co.ke';
const resolveKey = (key?: string) => {
  if (!key) return '';
  if (key.startsWith('http')) return key;
  // clean up malformed video keys (mirrors mobile logic)
  let k = key.replace(/-\d+(-poster\.jpg)?$/, '');
  if (k.includes('videos/') && !k.endsWith('.mp4')) k += '.mp4';
  return `${CDN}/${k.replace(/^\//, '')}`;
};

const getVideoUrl = (post: Post): string => {
  const videoKey = post.media_keys?.find(k => k.includes('videos/')) || post.media_keys?.[0];
  if (!videoKey || videoKey.includes('-poster.jpg')) return '';
  return post.media_urls?.primary || resolveKey(videoKey);
};

const getThumbnail = (post: Post): string =>
  post.media_urls?.thumbnails?.[0] ||
  resolveKey(post.thumbnail_keys?.[0]) ||
  '';

const getAvatar = (post: Post): string =>
  post.avatar_key ? getImageUrl(post.avatar_key) : '';

// ── Comments drawer ───────────────────────────────────────────────────────────

const CommentsDrawer: React.FC<{
  post: Post;
  onClose: () => void;
}> = ({ post, onClose }) => {
  const { postComments, commentsLoading, fetchComments, addComment, addCommentLoading } = useFeedStore();
  const [text, setText] = useState('');
  const comments = postComments[post.id] || [];

  useEffect(() => { fetchComments(post.id); }, [post.id]);

  const submit = async () => {
    if (!text.trim()) return;
    await addComment(post.id, text.trim());
    setText('');
  };

  return (
    // Slide-up drawer — sits over the right side of the reel
    <div className="absolute inset-0 z-30 flex flex-col justify-end" onClick={onClose}>
      <div
        className="bg-[#111] rounded-t-2xl max-h-[70%] flex flex-col"
        onClick={e => e.stopPropagation()}
        style={{ animation: 'reel-slide-up 0.22s ease-out' }}
      >
        {/* Handle */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="text-white font-bold text-sm">
            {post.comments_count.toLocaleString()} comments
          </span>
          <button onClick={onClose}><X size={18} className="text-white/60" /></button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {commentsLoading[post.id] && comments.length === 0 && (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="animate-spin text-white/40" />
            </div>
          )}
          {comments.map(c => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-white">
                {c.user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                <span className="text-white text-xs font-semibold">{c.user?.username} </span>
                <span className="text-white/80 text-xs">{c.text || c.content}</span>
                <p className="text-white/40 text-[10px] mt-0.5">{c.timestamp}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && !commentsLoading[post.id] && (
            <p className="text-white/40 text-sm text-center py-6">No comments yet. Be the first!</p>
          )}
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/10 flex gap-2">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="Add a comment…"
            className="flex-1 bg-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-white/30"
          />
          <button
            onClick={submit}
            disabled={!text.trim() || addCommentLoading[post.id]}
            className="w-9 h-9 rounded-full bg-primary flex items-center justify-center disabled:opacity-40"
          >
            {addCommentLoading[post.id]
              ? <Loader2 size={15} className="animate-spin text-white" />
              : <Send size={15} className="text-white" />
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Single reel item ──────────────────────────────────────────────────────────

interface ReelItemProps {
  post: Post;
  isActive: boolean;
  globalMuted: boolean;
  onToggleMute: () => void;
  onLike: (id: string) => void;
  onCommentOpen: (id: string) => void;
  onNext: () => void;
  onPrev: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

const ReelItem = memo<ReelItemProps>(({
  post, isActive, globalMuted, onToggleMute,
  onLike, onCommentOpen, onNext, onPrev, hasPrev, hasNext,
}) => {
  const videoRef   = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying]     = useState(false);
  const [progress, setProgress]   = useState(0);
  const [duration, setDuration]   = useState(0);
  const [showPlay, setShowPlay]   = useState(false);
  const [showComments, setShowComments] = useState(false);
  const isLiked = useFeedStore(s => s.isPostLiked(post.id));

  const videoUrl    = getVideoUrl(post);
  const thumbnail   = getThumbnail(post);
  const avatarUrl   = getAvatar(post);
  const isVideo     = post.media_type === 'video' || !!videoUrl;

  // Auto-play / pause based on active state
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    if (isActive) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
      setPlaying(false);
      setProgress(0);
    }
  }, [isActive, isVideo]);

  // Sync mute
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = globalMuted;
  }, [globalMuted]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else         { v.play(); setPlaying(true); }
    setShowPlay(true);
    setTimeout(() => setShowPlay(false), 800);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * v.duration;
  };

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center select-none">

      {/* ── Media ── */}
      <div className="relative h-full w-full" onClick={togglePlay}>
        {isVideo && videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={thumbnail}
            className="h-full w-full object-cover"
            loop
            playsInline
            muted={globalMuted}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={e => setDuration((e.target as HTMLVideoElement).duration)}
            onEnded={() => setPlaying(false)}
          />
        ) : thumbnail ? (
          <img src={thumbnail} alt={post.content} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-[#111]">
            <Video size={48} className="text-white/20" />
          </div>
        )}

        {/* Tap-to-play overlay */}
        {showPlay && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center" style={{ animation: 'reel-pop 0.4s ease-out forwards' }}>
              {playing ? <Play size={28} className="text-white fill-white" /> : <Pause size={28} className="text-white fill-white" />}
            </div>
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
      </div>

      {/* ── Bottom-left: creator info (TikTok style) ── */}
      <div className="absolute bottom-14 left-3 right-20 pointer-events-none">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full overflow-hidden border border-white/40 flex-shrink-0 bg-white/10">
            {avatarUrl
              ? <img src={avatarUrl} alt={post.username} className="w-full h-full object-cover" />
              : <span className="w-full h-full flex items-center justify-center text-white text-xs font-bold">{post.username?.[0]?.toUpperCase()}</span>
            }
          </div>
          <span className="text-white font-bold text-sm drop-shadow">@{post.username}</span>
          <span className="text-white/50 text-[10px]">
            {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
          </span>
        </div>
        {post.content && (
          <p className="text-white text-sm leading-snug drop-shadow line-clamp-2">{post.content}</p>
        )}
      </div>

      {/* ── Right-side actions (TikTok / Instagram style) ── */}
      <div className="absolute right-3 bottom-16 flex flex-col items-center gap-5">
        {/* Like */}
        <button
          onClick={e => { e.stopPropagation(); onLike(post.id); }}
          className="flex flex-col items-center gap-1"
        >
          <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-transform active:scale-90 ${isLiked ? 'bg-red-500/20' : 'bg-black/40'}`}>
            <Heart size={24} className={isLiked ? 'fill-red-500 text-red-500' : 'text-white'} />
          </div>
          <span className="text-white text-[11px] font-semibold drop-shadow">{fmt(post.likes_count)}</span>
        </button>

        {/* Comment */}
        <button
          onClick={e => { e.stopPropagation(); setShowComments(true); onCommentOpen(post.id); }}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center">
            <MessageCircle size={24} className="text-white" />
          </div>
          <span className="text-white text-[11px] font-semibold drop-shadow">{fmt(post.comments_count)}</span>
        </button>

        {/* Share */}
        <button
          onClick={e => { e.stopPropagation(); navigator.clipboard?.writeText(window.location.href); }}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center">
            <Share2 size={22} className="text-white" />
          </div>
          <span className="text-white text-[11px] font-semibold drop-shadow">Share</span>
        </button>

        {/* Mute / Unmute */}
        <button
          onClick={e => { e.stopPropagation(); onToggleMute(); }}
          className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center"
          title={globalMuted ? 'Unmute' : 'Mute'}
        >
          {globalMuted
            ? <VolumeX size={22} className="text-white" />
            : <Volume2 size={22} className="text-white" />
          }
        </button>
      </div>

      {/* ── Progress bar (YouTube Shorts style) ── */}
      {isVideo && (
        <div
          className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 cursor-pointer group"
          onClick={e => { e.stopPropagation(); handleScrub(e); }}
        >
          <div
            className="h-full bg-white transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
          {/* Scrubber thumb — visible on hover */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity -mt-0.5"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>
      )}

      {/* ── Prev / Next flanking buttons (YouTube Shorts style) ── */}
      {hasPrev && (
        <button
          onClick={e => { e.stopPropagation(); onPrev(); }}
          className="absolute top-1/2 -translate-y-1/2 -left-12 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:bg-black/80 transition-colors"
          title="Previous reel (↑)"
        >
          <ChevronUp size={20} className="text-white" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={e => { e.stopPropagation(); onNext(); }}
          className="absolute top-1/2 -translate-y-1/2 -right-12 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:bg-black/80 transition-colors"
          title="Next reel (↓)"
        >
          <ChevronDown size={20} className="text-white" />
        </button>
      )}

      {/* ── Comments drawer ── */}
      {showComments && (
        <CommentsDrawer post={post} onClose={() => setShowComments(false)} />
      )}
    </div>
  );
});

ReelItem.displayName = 'ReelItem';

// ── Main ReelsPage ────────────────────────────────────────────────────────────

export const ReelsPage: React.FC = () => {
  const { feedPosts, feedLoading, fetchFeed, loadMoreFeed, feedPagination, toggleLike } = useFeedStore();
  const [searchParams] = useSearchParams();
  const startIndex = parseInt(searchParams.get('index') || '0', 10) || 0;
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [muted, setMuted]               = useState(true);
  const [searchQuery, setSearchQuery]   = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs     = useRef<(HTMLDivElement | null)[]>([]);
  const searchRef    = useRef<HTMLInputElement>(null);

  // Filter video-only posts (mirrors mobile)
  const allReels = feedPosts.filter(p =>
    p.media_type === 'video' ||
    p.media_keys?.some(k => k.includes('videos/'))
  );

  // Apply search filter — by username or caption
  const reels = searchQuery.trim()
    ? allReels.filter(p =>
        p.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.content?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allReels;

  useEffect(() => { fetchFeed({ refresh: true }); }, []);

  // ── Keyboard navigation ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (searchFocused) return; // don't intercept when typing in search
      if (e.key === 'ArrowDown' || e.key === 'j') goTo(currentIndex + 1);
      if (e.key === 'ArrowUp'   || e.key === 'k') goTo(currentIndex - 1);
      if (e.key === 'm') setMuted(m => !m);
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, reels.length, searchFocused]);

  // Scroll to start index on mount
  useEffect(() => {
    if (startIndex > 0 && itemRefs.current[startIndex]) {
      itemRefs.current[startIndex]?.scrollIntoView();
    }
  }, [reels.length > 0]);

  // ── Intersection Observer ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = itemRefs.current.findIndex(r => r === entry.target);
            if (idx !== -1) setCurrentIndex(idx);
          }
        });
      },
      { threshold: 0.6 }
    );
    itemRefs.current.forEach(r => r && observer.observe(r));
    return () => observer.disconnect();
  }, [reels.length]);

  // Load more when near end
  useEffect(() => {
    if (currentIndex >= reels.length - 3 && feedPagination.has_more && !feedLoading) {
      loadMoreFeed();
    }
  }, [currentIndex, reels.length]);

  const goTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, reels.length - 1));
    itemRefs.current[clamped]?.scrollIntoView({ behavior: 'smooth' });
    setCurrentIndex(clamped);
  }, [reels.length]);

  // ── Loading skeleton ──
  if (feedLoading && allReels.length === 0) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center gap-4">
        <Loader2 size={36} className="animate-spin text-white/40" />
        <p className="text-white/40 text-sm">Loading reels…</p>
      </div>
    );
  }

  // ── Empty state ──
  if (!feedLoading && allReels.length === 0) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center gap-4 px-8 text-center">
        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-2">
          <Video size={36} className="text-white/30" />
        </div>
        <p className="text-white font-bold text-lg">No reels yet</p>
        <p className="text-white/40 text-sm">Video posts will appear here once people start sharing.</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes reel-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes reel-pop {
          0%   { transform: scale(0.6); opacity: 1; }
          60%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1);   opacity: 0; }
        }
      `}</style>

      {/* ── Search Reels bar — floats over the top of the reel ── */}
      <div className="fixed top-14 left-0 right-0 z-50 flex justify-center px-4 pt-3 pb-1 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-sm" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }}>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentIndex(0); }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search reels…"
              className="w-full pl-9 pr-9 py-2.5 rounded-full text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)' }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setCurrentIndex(0); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X size={14} className="text-white/60" />
              </button>
            )}
          </div>
          {!searchQuery && !searchFocused && (
            <p className="text-center text-[10px] text-white/25 mt-1">↑↓ navigate · M mute · / search</p>
          )}
        </div>
      </div>

      {/* No search results */}
      {searchQuery.trim() && reels.length === 0 && (
        <div className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center gap-3">
          <Search size={40} className="text-white/20" />
          <p className="text-white/50 text-sm">No reels found for “{searchQuery}”</p>
          <button onClick={() => setSearchQuery('')} className="text-white/40 text-xs underline">Clear search</button>
        </div>
      )}

      {/* ── Full-viewport snap scroll container ── */}
      <div
        ref={containerRef}
        className="h-screen overflow-y-scroll snap-y snap-mandatory bg-black"
        style={{ scrollbarWidth: 'none' }}
      >
        {reels.map((post, i) => (
          <div
            key={post.id}
            ref={el => { itemRefs.current[i] = el; }}
            className="snap-start h-screen w-full flex items-center justify-center relative"
          >
            <div
              className="relative h-full bg-black overflow-hidden"
              style={{ width: 'min(100%, calc(100vh * 9 / 16))' }}
            >
              <ReelItem
                post={post}
                isActive={i === currentIndex}
                globalMuted={muted}
                onToggleMute={() => setMuted(m => !m)}
                onLike={toggleLike}
                onCommentOpen={() => {}}
                onNext={() => goTo(i + 1)}
                onPrev={() => goTo(i - 1)}
                hasPrev={i > 0}
                hasNext={i < reels.length - 1}
              />
            </div>
          </div>
        ))}

        {feedLoading && reels.length > 0 && (
          <div className="h-20 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-white/40" />
          </div>
        )}

        {!feedPagination.has_more && reels.length > 0 && (
          <div className="h-20 flex items-center justify-center">
            <span className="text-white/30 text-sm">You've seen all reels 🎬</span>
          </div>
        )}
      </div>
    </>
  );
};

export default ReelsPage;
